import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IndianRupee } from 'lucide-react';
import FormPageLayout from '../components/common/FormPageLayout';
import Alert from '../components/common/Alert';
import DateInput from '../components/common/DateInput';
import CalculatorInput from '../components/common/CalculatorInput';
import MobileSelect from '../components/common/MobileSelect';
import { getIconComponent } from '../components/common/IconPicker';
import { groupCategories } from '../utils/categoryGroups';
import { useCategoriesQuery, useAccountsQuery, useCreateRecurringTransaction, useUpdateRecurringTransaction, useRecurringTransactionQuery } from '../hooks/useQueries';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/formatters';
import AddAccountModal from '../components/account/AddAccountModal';
import AddCategoryModal from '../components/category/AddCategoryModal';

const initialFormData = {
  description: '',
  type: 'income',
  category: '',
  account: '',
  amount: '',
  isTDSDeducted: false,
  tdsAmount: '',
  notes: '',
  fromAccount: '',
  toAccount: '',
  frequency: 'monthly',
  startDate: formatDate(new Date(), 'input'),
  endDate: '',
  isSubscription: false,
  reminderEnabled: true
};

const frequencyOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

export default function RecurringFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'income';

  const { toast } = useToast();

  // Fetch recurring transaction if editing
  const { data: recurringData } = useRecurringTransactionQuery(id, { enabled: !!id });
  const recurringTransaction = recurringData?.recurringTransaction || recurringData;

  const { data: categories = [] } = useCategoriesQuery();
  const { data: accounts = [] } = useAccountsQuery();
  const createMutation = useCreateRecurringTransaction();
  const updateMutation = useUpdateRecurringTransaction();

  const [formData, setFormData] = useState({ ...initialFormData, type: initialType });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);

  const handleAccountAdded = (newAccount) => {
    setShowAddAccountModal(false);
    if (newAccount?._id) {
      setFormData(prev => ({ ...prev, account: newAccount._id }));
    }
  };

  const handleCategoryAdded = (newCategory) => {
    setShowAddCategoryModal(false);
    if (newCategory?._id) {
      setFormData(prev => ({ ...prev, category: newCategory._id }));
    }
  };

  const isEditing = !!id;
  const isTransfer = formData.type === 'transfer';

  // Filter categories by type
  const incomeCategories = categories.filter(c => c.type === 'income');
  const expenseCategories = categories.filter(c => c.type === 'expense');
  const currentCategories = formData.type === 'income' ? incomeCategories : expenseCategories;

  // Filter active accounts
  const activeAccounts = accounts.filter(a => !a.isDeleted);

  // Build category options for MobileSelect — grouped like AddTransaction
  const { categoryOptions, categoryGroups } = useMemo(() => {
    const opts = [];
    const groupOrder = [];

    const { groups, categoriesByGroup } = groupCategories(currentCategories);
    groups.forEach(groupName => {
      groupOrder.push(groupName);
      categoriesByGroup[groupName].forEach(cat => {
        opts.push({
          value: cat._id, label: cat.name,
          icon: getIconComponent(cat.icon), iconColor: cat.color,
          group: groupName
        });
      });
    });

    return {
      categoryOptions: opts,
      categoryGroups: groupOrder.length > 0 ? groupOrder : null
    };
  }, [currentCategories]);

  // Build account options for MobileSelect
  const accountOptions = useMemo(() => {
    return activeAccounts.map(acc => ({
      value: acc._id,
      label: acc.name,
      icon: getIconComponent(acc.icon),
      iconColor: acc.color
    }));
  }, [activeAccounts]);

  // Build "to account" options (excluding selected "from" account)
  const toAccountMobileOptions = useMemo(() => {
    return activeAccounts
      .filter(acc => acc._id !== formData.fromAccount)
      .map(acc => ({
        value: acc._id,
        label: acc.name,
        icon: getIconComponent(acc.icon),
        iconColor: acc.color
      }));
  }, [activeAccounts, formData.fromAccount]);

  // Load recurring transaction data when editing
  useEffect(() => {
    if (recurringTransaction && isEditing) {
      setFormData({
        description: recurringTransaction.description || '',
        type: recurringTransaction.type,
        category: recurringTransaction.category?._id || recurringTransaction.category || '',
        account: recurringTransaction.account?._id || recurringTransaction.account || '',
        amount: recurringTransaction.amount != null ? parseFloat(recurringTransaction.amount.toFixed(2)).toString() : '',
        isTDSDeducted: recurringTransaction.isTDSDeducted || false,
        tdsAmount: recurringTransaction.tdsAmount?.toString() || '',
        notes: recurringTransaction.notes || '',
        fromAccount: recurringTransaction.type === 'transfer' ? (recurringTransaction.account?._id || recurringTransaction.account || '') : '',
        toAccount: recurringTransaction.toAccount?._id || recurringTransaction.toAccount || '',
        frequency: recurringTransaction.frequency || 'monthly',
        startDate: formatDate(recurringTransaction.startDate, 'input'),
        endDate: recurringTransaction.endDate ? formatDate(recurringTransaction.endDate, 'input') : '',
        isSubscription: recurringTransaction.isSubscription || false,
        reminderEnabled: recurringTransaction.reminderEnabled !== false
      });
    }
  }, [recurringTransaction, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (name === 'type') {
      setFormData((prev) => ({
        ...prev,
        type: value,
        category: '',
        isTDSDeducted: false,
        tdsAmount: '',
        ...(value !== 'transfer' && { fromAccount: '', toAccount: '' }),
        ...(value === 'transfer' && { account: '' })
      }));
    }

    const selectedCatName = name === 'category' ? categories.find(c => c._id === value)?.name : null;
    if (name === 'category' && selectedCatName !== 'Salary') {
      setFormData((prev) => ({
        ...prev,
        isTDSDeducted: false,
        tdsAmount: ''
      }));
    }

    if (name === 'isTDSDeducted' && !checked) {
      setFormData((prev) => ({
        ...prev,
        isTDSDeducted: false,
        tdsAmount: ''
      }));
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }

    if (isTransfer) {
      if (!formData.fromAccount) {
        errors.fromAccount = 'Please select source account';
      }
      if (!formData.toAccount) {
        errors.toAccount = 'Please select destination account';
      }
      if (formData.fromAccount && formData.toAccount && formData.fromAccount === formData.toAccount) {
        errors.toAccount = 'Source and destination must be different';
      }
    } else {
      if (!formData.description.trim()) {
        errors.description = 'Description is required';
      } else if (formData.description.length < 2) {
        errors.description = 'Description must be at least 2 characters';
      }

      if (!formData.category) {
        errors.category = 'Please select a category';
      }

      if (!formData.account) {
        errors.account = 'Please select an account';
      }

      if (formData.isTDSDeducted && !formData.tdsAmount) {
        errors.tdsAmount = 'TDS amount is required when TDS is deducted';
      }
    }

    if (formData.endDate && formData.startDate) {
      const start = new Date(formData.startDate.split('-').reverse().join('-'));
      const end = new Date(formData.endDate.split('-').reverse().join('-'));
      if (end <= start) {
        errors.endDate = 'End date must be after start date';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      const data = {
        type: formData.type,
        frequency: formData.frequency,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        amount: parseFloat(formData.amount),
        notes: formData.notes || undefined,
        reminderEnabled: formData.reminderEnabled
      };

      if (isTransfer) {
        data.fromAccount = formData.fromAccount;
        data.toAccount = formData.toAccount;
        data.description = formData.description || `Transfer: ${formData.fromAccount} → ${formData.toAccount}`;
      } else {
        data.description = formData.description;
        data.category = formData.category;
        data.account = formData.account;
        data.isSubscription = formData.type === 'expense' ? formData.isSubscription : false;

        const isSalaryCategory = categories.find(c => c._id === formData.category)?.name === 'Salary';
        const hasTDS = isSalaryCategory && formData.isTDSDeducted && formData.tdsAmount;
        data.isTDSDeducted = isSalaryCategory ? formData.isTDSDeducted : false;
        data.tdsAmount = hasTDS ? parseFloat(formData.tdsAmount) : 0;
      }

      if (isEditing) {
        await updateMutation.mutateAsync({ id, data });
        toast.success('Recurring transaction updated');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Recurring transaction created');
      }

      navigate('/recurring');
    } catch (err) {
      setError(err.error || err.message || 'Failed to save recurring transaction');
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = isEditing ? 'Edit Recurring Transaction' : 'Add Recurring Transaction';

  return (
    <FormPageLayout title={pageTitle} backPath="/recurring">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Transaction Type - Segmented control */}
        <div className="grid grid-cols-3 gap-1 p-1 bg-surface-100 rounded-xl">
          {[
            { value: 'income', label: 'Income', activeText: 'text-income-600' },
            { value: 'expense', label: 'Expense', activeText: 'text-expense-600' },
            { value: 'transfer', label: 'Transfer', activeText: 'text-primary-600' }
          ].map(tab => (
            <button
              key={tab.value}
              type="button"
              onClick={() => handleChange({ target: { name: 'type', value: tab.value } })}
              className={`py-2.5 px-2 rounded-lg font-medium text-sm transition-all ${
                formData.type === tab.value
                  ? `bg-white ${tab.activeText} shadow-sm`
                  : 'text-surface-500'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Frequency - Pill chips */}
        <div>
          <label className="label">Frequency</label>
          <div className="flex flex-wrap gap-2">
            {frequencyOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange({ target: { name: 'frequency', value: option.value } })}
                className={`py-2 px-4 rounded-full text-sm font-medium transition-all ${
                  formData.frequency === option.value
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Start & End Date */}
        <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
          <div>
            <label className="label">Start Date</label>
            <DateInput
              value={formData.startDate}
              onChange={(date) => {
                setFormData(prev => ({ ...prev, startDate: date }));
                if (validationErrors.startDate) {
                  setValidationErrors(prev => ({ ...prev, startDate: null }));
                }
              }}
              error={!!validationErrors.startDate}
            />
            {validationErrors.startDate && (
              <p className="text-expense-600 text-sm mt-1">{validationErrors.startDate}</p>
            )}
          </div>
          <div>
            <label className="label">End Date (Optional)</label>
            <DateInput
              value={formData.endDate}
              onChange={(date) => {
                setFormData(prev => ({ ...prev, endDate: date }));
                if (validationErrors.endDate) {
                  setValidationErrors(prev => ({ ...prev, endDate: null }));
                }
              }}
              error={!!validationErrors.endDate}
            />
            {validationErrors.endDate && (
              <p className="text-expense-600 text-sm mt-1">{validationErrors.endDate}</p>
            )}
            <p className="text-surface-500 text-xs mt-1">Leave empty for indefinite</p>
          </div>
        </div>

        {/* Transfer-specific fields */}
        {isTransfer ? (
          <>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <label className="label">From</label>
                <MobileSelect
                  value={formData.fromAccount}
                  onChange={(value) => handleChange({ target: { name: 'fromAccount', value } })}
                  options={accountOptions}
                  placeholder={activeAccounts.length === 0 ? 'No accounts' : 'Select source'}
                  label="From Account"
                  columns={3}
                  size="sm"
                  showIconInTrigger
                  error={!!validationErrors.fromAccount}
                  actionButton={{
                    label: 'Add New',
                    onClick: () => setShowAddAccountModal(true)
                  }}
                />
                {validationErrors.fromAccount && (
                  <p className="text-expense-600 text-sm mt-1">{validationErrors.fromAccount}</p>
                )}
              </div>

              <span className="text-surface-400 mt-6 flex-shrink-0">→</span>

              <div className="flex-1 min-w-0">
                <label className="label">To</label>
                <MobileSelect
                  value={formData.toAccount}
                  onChange={(value) => handleChange({ target: { name: 'toAccount', value } })}
                  options={toAccountMobileOptions}
                  placeholder={activeAccounts.length === 0 ? 'No accounts' : 'Select destination'}
                  label="To Account"
                  columns={3}
                  size="sm"
                  showIconInTrigger
                  error={!!validationErrors.toAccount}
                />
                {validationErrors.toAccount && (
                  <p className="text-expense-600 text-sm mt-1">{validationErrors.toAccount}</p>
                )}
              </div>
            </div>

            <div>
              <label className="label">Amount (₹) <span className="text-surface-400 font-normal">- supports math: 100+50</span></label>
              <CalculatorInput
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00 or 100+50"
                className="input"
                icon={IndianRupee}
                error={!!validationErrors.amount}
              />
              {validationErrors.amount && (
                <p className="text-expense-600 text-sm mt-1">{validationErrors.amount}</p>
              )}
            </div>

            <div>
              <label className="label">Description (Optional)</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="e.g., Monthly savings transfer"
                className="input"
              />
            </div>

            <div>
              <label className="label">Notes (Optional)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Additional notes..."
                className="input resize-none"
              />
            </div>

            {/* Reminder Toggle (transfers) */}
            <div className="flex items-center justify-between p-3 bg-surface-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-xl">🔔</span>
                <div>
                  <p className="text-sm font-medium text-surface-800">Reminders</p>
                  <p className="text-xs text-surface-500">Notify 10, 5, 3, 1 days before</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleChange({ target: { name: 'reminderEnabled', type: 'checkbox', checked: !formData.reminderEnabled } })}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  formData.reminderEnabled ? 'bg-primary-600' : 'bg-surface-300'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  formData.reminderEnabled ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Category & Account */}
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
              <div>
                <label className="label">Category</label>
                <MobileSelect
                  value={formData.category}
                  onChange={(value) => handleChange({ target: { name: 'category', value } })}
                  options={categoryOptions}
                  groups={categoryGroups}
                  placeholder={currentCategories.length === 0 ? 'Tap to add category' : 'Select category'}
                  label="Category"
                  columns={3}
                  size="sm"
                  showIconInTrigger
                  error={!!validationErrors.category}
                  actionButton={{
                    label: 'Add New',
                    onClick: () => setShowAddCategoryModal(true)
                  }}
                />
                {validationErrors.category && (
                  <p className="text-expense-600 text-sm mt-1">{validationErrors.category}</p>
                )}
              </div>

              <div>
                <label className="label">Account</label>
                <MobileSelect
                  value={formData.account}
                  onChange={(value) => handleChange({ target: { name: 'account', value } })}
                  options={accountOptions}
                  placeholder={activeAccounts.length === 0 ? 'Tap to add account' : 'Select account'}
                  label="Account"
                  columns={2}
                  size="sm"
                  showIconInTrigger
                  error={!!validationErrors.account}
                  actionButton={{
                    label: 'Add New',
                    onClick: () => setShowAddAccountModal(true)
                  }}
                />
                {validationErrors.account && (
                  <p className="text-expense-600 text-sm mt-1">{validationErrors.account}</p>
                )}
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="label">Amount (₹) <span className="text-surface-400 font-normal">- supports math: 100+50</span></label>
              <CalculatorInput
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00 or 100+50"
                className="input"
                icon={IndianRupee}
                error={!!validationErrors.amount}
              />
              {validationErrors.amount && (
                <p className="text-expense-600 text-sm mt-1">{validationErrors.amount}</p>
              )}
            </div>

            {/* Subscription Toggle (only for expense) */}
            {formData.type === 'expense' && (
              <div className="flex items-center justify-between p-3 bg-surface-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-xl">🏷️</span>
                  <div>
                    <p className="text-sm font-medium text-surface-800">Subscription / Autopay</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleChange({ target: { name: 'isSubscription', type: 'checkbox', checked: !formData.isSubscription } })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    formData.isSubscription ? 'bg-primary-600' : 'bg-surface-300'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    formData.isSubscription ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            )}

            {/* Reminder Toggle (all types) */}
            <div className="flex items-center justify-between p-3 bg-surface-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-xl">🔔</span>
                <div>
                  <p className="text-sm font-medium text-surface-800">Reminders</p>
                  <p className="text-xs text-surface-500">Notify 10, 5, 3, 1 days before</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleChange({ target: { name: 'reminderEnabled', type: 'checkbox', checked: !formData.reminderEnabled } })}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  formData.reminderEnabled ? 'bg-primary-600' : 'bg-surface-300'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  formData.reminderEnabled ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>

            {/* Description */}
            <div>
              <label className="label">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="e.g., Monthly Salary, Netflix Subscription"
                className={`input ${validationErrors.description ? 'input-error' : ''}`}
              />
              {validationErrors.description && (
                <p className="text-expense-600 text-sm mt-1">{validationErrors.description}</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="label">Notes (Optional)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Additional notes..."
                className="input resize-none"
              />
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-surface-200">
          <button
            type="button"
            onClick={() => navigate('/recurring')}
            className="btn-secondary flex-1"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`flex-1 ${
              isTransfer
                ? 'btn-primary'
                : formData.type === 'income'
                ? 'btn-income'
                : 'btn-expense'
            }`}
            disabled={loading}
          >
            {loading ? 'Saving...' : isEditing ? 'Update' : 'Add Recurring'}
          </button>
        </div>
      </form>

      {/* Add Account Modal */}
      <AddAccountModal
        isOpen={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        onSuccess={handleAccountAdded}
      />

      {/* Add Category Modal */}
      <AddCategoryModal
        isOpen={showAddCategoryModal}
        onClose={() => setShowAddCategoryModal(false)}
        onSuccess={handleCategoryAdded}
      />
    </FormPageLayout>
  );
}
